import React, { useState } from "react";
import { Button, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import CommentBody from "./CommentBody";
import CommentEditor from "./CommentEditor";
import CommentReactions from "./CommentReactions";
import { UserDetailIcon } from "../UserDetailIcon";
import { HubButton } from "../inputs";

dayjs.extend(relativeTime);

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const CommentItem = ({
  comment,
  currentUserId,
  users = [],
  onReply,
  onEdit,
  onDelete,
  onReaction,
  hideReply = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [replying, setReplying] = useState(false);

  const isAuthor = comment.author?.id === currentUserId;
  const timestamp = dayjs(comment.createdAt).fromNow();

  const handleEdit = (body) => {
    onEdit(comment.id, body);
    setEditing(false);
  };

  const handleReply = (body) => {
    onReply(comment.id, body);
    setReplying(false);
  };

  return (
    <Stack gap="6px">
      <Stack gap="4px">
        <Stack
          direction="row"
          gap="8px"
          justifyContent="space-between"
          alignItems="center"
        >
          <UserDetailIcon
            size={24}
            user={{
              name: comment.author.name,
              hueShift: comment.author.id,
              email: timestamp,
            }}
            variant="rounded"
          />

          {!comment.deleted && !editing && (
            <Stack direction="row" gap="4px" alignItems="center">
              {!hideReply && (
                <HubButton
                  size="small"
                  color="neutral"
                  icon="ExternalLink"
                  // label="Reply"
                  iconProps={{ size: 12 }}
                  onClick={() => setReplying(!replying)}
                  sx={{
                    fontSize: 10,
                    textTransform: "none",
                    minWidth: 0,
                    p: "2px 6px",
                  }}
                />
              )}
              {isAuthor && (
                <>
                  <HubButton
                    size="small"
                    color="neutral"
                    icon="Edit"
                    // label="Edit"
                    iconProps={{ size: 12 }}
                    onClick={() => setEditing(true)}
                    sx={{
                      fontSize: 10,
                      textTransform: "none",
                      minWidth: 0,
                      p: "2px 6px",
                    }}
                  />
                  <HubButton
                    size="small"
                    color="error"
                    icon="Trash2"
                    // label="Delete"
                    iconProps={{ size: 12 }}
                    onClick={() => onDelete(comment.id)}
                    sx={{
                      fontSize: 10,
                      textTransform: "none",
                      minWidth: 0,
                      p: "2px 6px",
                    }}
                  />
                </>
              )}
            </Stack>
          )}
        </Stack>

        {comment.editedAt && (
          <Typography variant="caption" fontSize={10} color="text.disabled">
            (edited)
          </Typography>
        )}
      </Stack>

      {editing ? (
        <CommentEditor
          users={users}
          initialContent={comment.body}
          onSubmit={handleEdit}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <CommentBody body={comment.deleted ? null : comment.body} />
      )}

      {!comment.deleted && comment.reactions?.length > 0 && (
        <CommentReactions
          reactions={comment.reactions}
          currentUserId={currentUserId}
          onToggle={(emoji) => onReaction(comment.id, emoji)}
        />
      )}

      {replying && (
        <Stack pl="32px">
          <CommentEditor
            users={users}
            onSubmit={handleReply}
            onCancel={() => setReplying(false)}
            placeholder="Write a reply..."
          />
        </Stack>
      )}
    </Stack>
  );
};

export default CommentItem;
